import { StatusCodes } from 'http-status-codes'

import { loginLink } from '../router'

import { makeEnvironmentModel } from './makeEnvironmentModel'
import { MeasurementUnitsService } from './MeasurementUnitsService'
import { ENV, GCS_ENV_NAME, OVERSIGHT_ENV_NAME } from '../params'
import fetchWithTimeout from 'services/fetchWithTImeout'
import type { FetchWithTimeoutOptions } from 'services/fetchWithTImeout'

export interface FetchModelEnvironment extends Pick<typeof window, 'fetch' | 'setTimeout' | 'clearTimeout'> {}

export const NETWORK_ERROR_MESSAGE = 'Network error. Please check your connection and try again'

export const isNetworkError = (error: unknown): boolean => {
  return !navigator.onLine ||
    (error as Error)?.message === 'Failed to fetch' ||
    (error as Error)?.message?.includes('NetworkError');
}

const defaultRequestInit = {
  credentials: 'include',
} as const

interface FetchResult {
  response?: Response
  error?: Error
  hasError: boolean
  didAbort: boolean
  didTimeout: boolean
  isNetworkError: boolean
}

export interface FetchLoggingParams {
  fetchOperationName: string
  context?: Record<string, unknown>
}

export interface FetchResponseError {
  message: string
  extra: string | null
}

interface LoadError {
  result?: never
  error: Error
  responseStatus?: number,
}

interface LoadSuccess<T> {
  result: T
  error?: never,
  responseStatus?: number,
}

export type LoadObjectResult<T> = LoadError | LoadSuccess<T>

export const FetchModel = makeEnvironmentModel<FetchModelEnvironment>('FetchModel')
  .views((_self) => ({
    get baseUrl(): string {
      const { protocol, hostname } = window.location
      const isNewCenterUrl = hostname.startsWith('center.')
      let apiHostname: string
      if (ENV === 'dev') {
        apiHostname = 'localhost'
      } else if (isNewCenterUrl) {
        apiHostname = `${GCS_ENV_NAME}.gcs-staging.flytrex.com`
      } else {
        // TODO: Drop this when all GCS are upgraded to 2.18 and CORS setting is enabled
        apiHostname = `api-${GCS_ENV_NAME}.flytrex.com`
      }
      return `${protocol}//${apiHostname}:8000/api/v1/`
    },
    get oversightBaseUrl(): string {
      const { protocol } = window.location
      const apiHostname = ENV === 'dev' ? 'localhost' : `api-${OVERSIGHT_ENV_NAME}.flytrex.com`
      return `${protocol}//${apiHostname}:7701/api/v1/`
    }
  }))
  .actions((self) => ({
    async fetch(
      url: RequestInfo,
      requestInit: FetchWithTimeoutOptions = defaultRequestInit,
    ): Promise<FetchResult> {
      let responseOrError: Response | Error | null = null
      const didTimeoutFlag = { didTimeout: false }

      if (requestInit.body && !requestInit.headers && !(requestInit.body instanceof FormData)) {
        requestInit.headers = new Headers({ 'Content-Type': 'application/json' })
      }

      try {
        responseOrError = await fetchWithTimeout(url, didTimeoutFlag, {
          ...defaultRequestInit,
          ...requestInit,
        })
      } catch (ex) {

        const context = { ex, url, requestInit, self } as const
        // https://stackoverflow.com/a/61113372/915875
        if (isNetworkError(ex as Error)) {
          console.warn(
            `Failed to [${requestInit.method ?? 'GET'}] URL '${url.toString()}' due to network error.`,
            context,
          )

          return {
            error: ex as Error,
            hasError: true,
            didAbort: false,
            isNetworkError: true,
            ...didTimeoutFlag,
          }
        }
        // https://developers.google.com/web/updates/2017/09/abortable-fetch#reacting_to_an_aborted_fetch
        if ((ex as Error).name === 'AbortError') {
          // Workaround for Uncaught `Error: The error you provided does not contain a stack trace`
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions -- This assignment is ok
          (ex as Error).stack = (ex as Error).stack || 'fake--not-an-actual-stack-trace'
          return {
            error: ex as Error,
            hasError: true,
            didAbort: !didTimeoutFlag.didTimeout,
            isNetworkError: false,
            ...didTimeoutFlag,
          }
        }

      }

      return {
        response: responseOrError as Response,
        hasError: false,
        didAbort: false,
        isNetworkError: false,
        ...didTimeoutFlag,
      }
    },
  }))
  .actions((self) => ({
    async loadObjectOrError<T>(
      url: RequestInfo,
      fetchOptions: FetchWithTimeoutOptions,
      _loggingInfo: FetchLoggingParams,
    ): Promise<LoadObjectResult<T>> {
      const { response, error, didTimeout } = await self.fetch(url, fetchOptions)

      if (response?.ok) {
        const text = await response.text()
        const result = text ? { result: JSON.parse(text) as T } : { result: text as unknown as T }
        return {
          ...result,
          responseStatus: response?.status,
        }
      } else {
        if (didTimeout) {
          return {
            error: { ...error, name: 'TimeoutError', message: 'The request timed out' },
            responseStatus: response?.status,
          }
        }

        if (error) {
          return { 
            error: { ...error, name: 'NetworkError', message: NETWORK_ERROR_MESSAGE },
            responseStatus: response?.status,
          }
        }

        if (response?.status === StatusCodes.UNAUTHORIZED) {
          loginLink.replace()

          return {
            error: {
              name: 'UnauthorizedError',
              message: 'User unauthorized, probably not logged in',
            },
            responseStatus: response?.status,
          }
        }

        const responseText = await response?.text()

        let serverErrorMessage: string | null = null

        if (responseText) {
          const responseObject = JSON.parse(responseText) as { message?: string, error?: string, extra?: unknown }
          // Try to extract error message from response using `message` and `extra` fields
          serverErrorMessage = responseObject.message || responseObject.error || null
          if (responseObject.message && responseObject.extra) {
            try {
              serverErrorMessage = MeasurementUnitsService.formatServerResponse(responseObject.message, responseObject.extra)
            } catch (err) {
              console.warn('Failed to format server response, keeping message unformatted', err)
            }
          }
        }

        return {
          error: {
            name: 'BadResponseError',
            message: serverErrorMessage || responseText || response?.statusText || 'Unknown error',
          },
          responseStatus: response?.status,
        }
      }
    },
  }))

